import { useEffect, useRef, useState } from "react";
import { Loader2, Download, AlertCircle, CheckCircle } from "lucide-react";
import api from "../../services/api";

/**
 * FetchCommitteeButton — Pro-only "fetch all reports for this committee" trigger.
 *
 * UX (placeholder copy — visual/text refinement is a follow-up pass):
 *   1. Renders a button on the committee row.
 *   2. Disabled when the user is not Pro tier (with explanatory tooltip).
 *   3. Click → confirm modal → POST /api/reports/fetch_committee.
 *   4. Polls GET /api/reports/fetch_committee/{job_id} every 5s while the job
 *      is queued or running, then renders a final status pill.
 *   5. On mount, GET /api/reports/fetch_committee/by_mecid/{mecid}/latest so
 *      we can show "Last fetched 2h ago" or pick up an in-flight job.
 *
 * Props:
 *   mecid: string — committee MEC ID, required
 *   userTier: string — 'free' | 'basic' | 'pro'
 *   onComplete?: () => void — called when a job finishes successfully so the
 *     parent can refresh the committee row's report counts
 */
export default function FetchCommitteeButton({ mecid, userTier, onComplete }) {
  const [job, setJob] = useState(null);            // latest job for this MECID
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const pollTimer = useRef(null);

  const isPro = userTier === "pro";
  const inFlight = job && (job.status === "queued" || job.status === "running");

  // Initial load + polling
  useEffect(() => {
    if (!mecid) return;
    fetchLatest();
    return () => stopPolling();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mecid]);

  // Poll while in flight
  useEffect(() => {
    if (inFlight) startPolling();
    else stopPolling();
    return () => stopPolling();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inFlight, job?.job_id]);

  const fetchLatest = async () => {
    try {
      const res = await api.get(`/api/reports/fetch_committee/by_mecid/${mecid}/latest`);
      setJob(res.data || null);
    } catch (err) {
      // 404 / no-job is fine; surface only real errors
      if (err.response?.status && err.response.status !== 404) {
        console.warn("fetch latest job failed:", err);
      }
    }
  };

  const startPolling = () => {
    stopPolling();
    pollTimer.current = setInterval(async () => {
      try {
        if (!job?.job_id) return;
        const res = await api.get(`/api/reports/fetch_committee/${job.job_id}`);
        const updated = res.data;
        setJob(updated);
        if (updated && (updated.status === "done" || updated.status === "partial")) {
          if (typeof onComplete === "function") onComplete();
        }
      } catch (err) {
        console.warn("poll failed:", err);
      }
    }, 5000);
  };

  const stopPolling = () => {
    if (pollTimer.current) {
      clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    setError("");
    try {
      const res = await api.post("/api/reports/fetch_committee", { mecid });
      setJob(res.data);
      setShowConfirm(false);
    } catch (err) {
      const detail = err.response?.data?.detail || "Could not start fetch.";
      setError(detail);
    } finally {
      setSubmitting(false);
    }
  };

  // ---- render ----
  if (!isPro) {
    return (
      <button
        className="btn-outline small"
        disabled
        title="Fetching all reports for a committee is a Pro feature."
        style={{ opacity: 0.5, cursor: "not-allowed", whiteSpace: "nowrap" }}
      >
        Fetch all (Pro)
      </button>
    );
  }

  // Pill describing the latest job's state, if any
  const Pill = () => {
    if (!job) return null;
    let bg = "#e5e7eb", color = "#374151", icon = null, text = job.status;
    if (job.status === "queued") {
      bg = "#fef3c7"; color = "#92400e"; icon = <Loader2 size={12} className="spin" />;
      text = "Queued";
    } else if (job.status === "running") {
      bg = "#dbeafe"; color = "#1e40af"; icon = <Loader2 size={12} className="spin" />;
      text = "Fetching…";
    } else if (job.status === "done") {
      bg = "#d1fae5"; color = "#065f46"; icon = <CheckCircle size={12} />;
      text = `Done (${job.downloaded_count})`;
    } else if (job.status === "partial") {
      bg = "#fef3c7"; color = "#92400e"; icon = <AlertCircle size={12} />;
      text = `Partial (${job.downloaded_count}+, ${job.failure_count} unavailable)`;
    } else if (job.status === "failed") {
      bg = "#fee2e2"; color = "#991b1b"; icon = <AlertCircle size={12} />;
      text = "Failed";
    }
    return (
      <span style={{
        display: "inline-flex", alignItems: "center", gap: "0.3rem",
        background: bg, color, padding: "0.15rem 0.5rem",
        borderRadius: 999, fontSize: "0.75rem", fontWeight: 600,
      }}>
        {icon}{text}
      </span>
    );
  };

  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}>
      <Pill />
      <button
        className="btn-outline small"
        onClick={(e) => { e.stopPropagation(); setShowConfirm(true); }}
        disabled={inFlight || submitting}
        style={{
          opacity: inFlight || submitting ? 0.6 : 1,
          cursor: inFlight || submitting ? "not-allowed" : "pointer",
          display: "inline-flex", alignItems: "center", gap: "0.35rem",
          whiteSpace: "nowrap",
        }}
        title={inFlight ? "Fetch already in progress" : "Fetch all reports for this committee"}
      >
        <Download size={14} />
        Fetch all
      </button>

      {showConfirm && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div style={{
            background: "white", padding: "1.5rem", borderRadius: 12,
            maxWidth: 440, width: "92%",
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          }}>
            <h3 style={{ margin: 0, fontSize: "1.1rem" }}>
              Fetch all reports for {mecid}?
            </h3>
            <p style={{ color: "#4b5563", fontSize: "0.9rem", marginTop: "0.75rem" }}>
              We'll pull every available report for this committee from the
              Missouri Ethics Commission and add them to your dashboard. This
              usually takes 10–40 minutes. We'll email you when it's done.
            </p>
            {error && (
              <p style={{
                background: "#fee2e2", color: "#991b1b", padding: "0.6rem",
                borderRadius: 8, fontSize: "0.85rem", marginTop: "0.75rem",
              }}>
                {error}
              </p>
            )}
            <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end", marginTop: "1rem" }}>
              <button
                className="btn-outline small"
                onClick={() => { setShowConfirm(false); setError(""); }}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                className="btn-primary small"
                onClick={handleConfirm}
                disabled={submitting}
              >
                {submitting ? <Loader2 size={14} className="spin" /> : "Start fetch"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
